import {
  CHAT_ERROR_CODES,
  DOCGEN_ERROR_CODES,
  QUOTA_ERROR_CODES,
} from '@shared/constants/error-codes';
import { ApiError } from '@/lib/api-error';

export const CHAT_CLIENT_ERROR_CODES = {
  CONCURRENT_TURN_LIMIT: 'CHAT_CONCURRENT_TURN_LIMIT',
} as const;

export const CHAT_CONCURRENT_TURN_LIMIT_MESSAGE = 'Vous avez déjà trois réponses en cours. Attendez qu’une réponse se termine avant d’en lancer une autre.';

const CHAT_ERROR_MESSAGES: Record<string, string> = {
  ASK_USER_PENDING_ATTACHMENTS: 'Répondez d’abord à la question ci-dessus avant d’envoyer des fichiers.',
  AUTH_REQUIRED: 'Authentification requise.',
  ATTACHMENT_TOO_LARGE: 'Le fichier dépasse la limite autorisée.',
  EMPTY_USER_MESSAGE: 'Le message ne peut pas être vide.',
  FORBIDDEN: 'Cette action n’est pas autorisée.',
  INTERNAL_ERROR: 'Une erreur est survenue pendant le traitement du chat.',
  INVALID_CHAT_SEQUENCE: 'Le message ne peut pas être envoyé dans cet état.',
  MISTRAL_RATE_LIMITED: 'Le service de génération est momentanément limité. Réessayez dans quelques instants.',
  MISTRAL_NOT_CONFIGURED: 'Le service de génération n’est pas disponible sur cet environnement.',
  NOT_FOUND: 'Conversation introuvable.',
  VALIDATION_ERROR: 'La requête de chat est invalide.',
  [CHAT_CLIENT_ERROR_CODES.CONCURRENT_TURN_LIMIT]: CHAT_CONCURRENT_TURN_LIMIT_MESSAGE,
  [CHAT_ERROR_CODES.CHAT_TURN_NOT_FOUND]: 'Ce tour de chat n’est plus actif.',
  [CHAT_ERROR_CODES.CONVERSATION_NOT_FOUND]: 'Conversation introuvable.',
  [CHAT_ERROR_CODES.DOCUMENT_INDEXING_FAILED]: 'L’indexation du document a échoué.',
  [CHAT_ERROR_CODES.DOCUMENT_EXPORT_ENGINE_UNAVAILABLE]: 'Le moteur de conversion PDF est indisponible. Téléchargez la version Word.',
  [CHAT_ERROR_CODES.DOCUMENT_EXPORT_FAILED]: 'Le document n’a pas pu être exporté.',
  [CHAT_ERROR_CODES.DOCUMENT_EXPORT_FORMAT_UNSUPPORTED]: 'Ce format d’export n’est pas encore disponible.',
  [CHAT_ERROR_CODES.DOCUMENT_EXPORT_NOT_FOUND]: 'Ce document n’est plus disponible pour l’export.',
  [CHAT_ERROR_CODES.DOCUMENT_DELETE_FAILED]: 'Le document indexé n’a pas pu être supprimé du moteur documentaire.',
  [CHAT_ERROR_CODES.DOCUMENT_DUPLICATE_NAME]: 'Un document avec le même nom existe déjà dans la base documentaire.',
  [CHAT_ERROR_CODES.DOCUMENT_FILE_UNAVAILABLE]: 'Ce document ne peut pas être ouvert : fichier source indisponible.',
  [CHAT_ERROR_CODES.DOCUMENT_ACCESS_DENIED]: 'Cette source n’est plus accessible avec vos autorisations actuelles.',
  [CHAT_ERROR_CODES.DOCUMENT_RANGE_INVALID]: 'La portion demandée du document n’est pas disponible. Rechargez la source.',
  [CHAT_ERROR_CODES.DOCUMENT_UPLOAD_FAILED]: 'Le document n’a pas pu être ajouté.',
  [CHAT_ERROR_CODES.MESSAGE_SEND_FAILED]: 'Le message n’a pas pu être envoyé.',
  [CHAT_ERROR_CODES.MISTRAL_UNAVAILABLE]: 'Le service de génération est temporairement indisponible.',
  [CHAT_ERROR_CODES.PAGEINDEX_UNAVAILABLE]: 'Le service documentaire est temporairement indisponible.',
  [CHAT_ERROR_CODES.RAG_DOCUMENT_NOT_INDEXED]: 'Ce document n’est pas encore indexé. Réessayez plus tard.',
  [CHAT_ERROR_CODES.RAG_EMBEDDING_FAILED]: 'Erreur lors de l’analyse documentaire. Réessayez.',
  [CHAT_ERROR_CODES.RAG_ENGINE_UNAVAILABLE]: 'Le service de recherche est temporairement indisponible.',
  [CHAT_ERROR_CODES.RAG_HYBRID_RETRIEVAL_FAILED]: 'Le moteur de recherche documentaire est temporairement indisponible. Réessayez dans quelques instants.',
  [CHAT_ERROR_CODES.RAG_INDEXING_EMPTY_CHUNKS]: 'Le document ne contient aucun contenu indexable.',
  [CHAT_ERROR_CODES.RAG_LEGACY_CROSS_TENANT]: 'Documents incompatibles entre entreprises. Contactez votre administrateur.',
  [CHAT_ERROR_CODES.RAG_LEGACY_DOC_IDS_UNRESOLVED]: 'Certains documents ne sont pas encore indexés dans le nouveau moteur. Réessayez plus tard.',
  [CHAT_ERROR_CODES.RAG_NO_COMPANY]: 'Votre compte n’est pas rattaché à une entreprise. Contactez un administrateur.',
  [CHAT_ERROR_CODES.RAG_NO_INDEXED_DOCUMENTS]: 'Active le RAG uniquement après avoir indexé au moins un document.',
  [CHAT_ERROR_CODES.RAG_PAYLOAD_AMBIGUOUS]: 'Requête mal formée. Veuillez réessayer.',
  [CHAT_ERROR_CODES.RAG_RERANK_FAILED]: 'Erreur lors du tri des résultats. Réessayez.',
  [CHAT_ERROR_CODES.QUOTA_EXCEEDED]: 'Vous avez atteint la limite de votre compte démo.',
  [QUOTA_ERROR_CODES.COMPANY_QUOTA_EXCEEDED]: 'Le quota de votre entreprise est épuisé.',
  [CHAT_ERROR_CODES.CHAT_ATTACHMENT_TOO_LARGE]: 'Le fichier dépasse la taille maximale autorisée.',
  [CHAT_ERROR_CODES.CHAT_ATTACHMENT_TOO_MANY]: 'Trop de pièces jointes dans le message.',
  [CHAT_ERROR_CODES.CHAT_ATTACHMENT_UNSUPPORTED]: 'Ce format de fichier n’est pas supporté dans le chat.',
  [CHAT_ERROR_CODES.CHAT_ATTACHMENT_NOT_FOUND]: 'Pièce jointe introuvable.',
  [CHAT_ERROR_CODES.CHAT_ATTACHMENT_NOT_READY]: 'La pièce jointe est encore en cours de traitement.',
  [CHAT_ERROR_CODES.CHAT_ATTACHMENT_EXTRACTION_FAILED]: 'Impossible d’extraire le contenu du document joint.',
  [CHAT_ERROR_CODES.CHAT_ATTACHMENT_EXTRACTION_EMPTY]: 'Le document joint ne contient pas de texte exploitable.',
  [CHAT_ERROR_CODES.CHAT_ATTACHMENT_UPLOAD_RATE_LIMIT]: 'Trop de pièces jointes envoyées récemment. Réessayez plus tard.',
  [CHAT_ERROR_CODES.WEB_TOOLS_DISABLED]: 'La recherche web est désactivée sur cet environnement.',
  [CHAT_ERROR_CODES.WEB_TOOLS_FETCH_BLOCKED]: 'Cette page web ne peut pas être consultée pour des raisons de sécurité.',
  [CHAT_ERROR_CODES.WEB_TOOLS_FETCH_EMPTY]: 'La page web ne contient pas de contenu exploitable.',
  [CHAT_ERROR_CODES.WEB_TOOLS_FETCH_FAILED]: 'La lecture de la page web a échoué. Réessayez.',
  [CHAT_ERROR_CODES.WEB_TOOLS_QUOTA_EXCEEDED]: 'Le quota de recherche web a été atteint. Réessayez plus tard.',
  [CHAT_ERROR_CODES.WEB_TOOLS_SEARCH_FAILED]: 'La recherche web a échoué. Réessayez.',
  [CHAT_ERROR_CODES.WEB_TOOLS_UNAVAILABLE]: 'Le service de recherche web est temporairement indisponible.',
  [CHAT_ERROR_CODES.WEB_TOOLS_URL_INVALID]: 'L’URL demandée n’est pas valide.',
  [DOCGEN_ERROR_CODES.DOCGEN_DISABLED]: 'La génération de documents riches est désactivée sur cet environnement.',
  [DOCGEN_ERROR_CODES.DOCGEN_BRIEF_INVALID]: 'Le brief du document est invalide. Précisez votre demande puis réessayez.',
  [DOCGEN_ERROR_CODES.DOCGEN_COMPLIANCE_FAILED]: 'Le document généré ne respecte pas toutes les contraintes demandées.',
  [DOCGEN_ERROR_CODES.DOCGEN_EXEC_FAILED]: 'Le document riche n’a pas pu être généré.',
  [DOCGEN_ERROR_CODES.DOCGEN_EXEC_TIMEOUT]: 'La génération du document a dépassé le temps autorisé.',
  [DOCGEN_ERROR_CODES.DOCGEN_GENERATION_FAILED]: 'La génération du document riche a échoué.',
  [DOCGEN_ERROR_CODES.DOCGEN_OUTPUT_TOO_LARGE]: 'Le document généré dépasse la taille autorisée.',
  [DOCGEN_ERROR_CODES.DOCGEN_QUOTA_EXCEEDED]: 'Le quota de génération documentaire a été atteint. Réessayez plus tard.',
  [DOCGEN_ERROR_CODES.DOCGEN_REVISION_CONFLICT]: 'Une version plus récente du document existe. Rechargez-la avant de modifier.',
  [DOCGEN_ERROR_CODES.DOCGEN_REVISION_TARGET_NOT_FOUND]: 'Le document à modifier est introuvable ou n’est plus accessible.',
  [DOCGEN_ERROR_CODES.DOCGEN_REVISION_UNSUPPORTED_LEGACY]: 'Cet ancien document ne peut pas être modifié directement. Créez une nouvelle version.',
  [DOCGEN_ERROR_CODES.DOCGEN_SERVICE_UNAVAILABLE]: 'Le service de génération documentaire est temporairement indisponible.',
  [DOCGEN_ERROR_CODES.DOCGEN_UNSUPPORTED_KIND]: 'Ce format de document riche n’est pas supporté.',
  [DOCGEN_ERROR_CODES.DOCGEN_VALIDATION_FAILED]: 'Le document généré n’a pas passé le contrôle qualité (mise en page, listes ou KPI). Relancez la génération.',
  [CHAT_ERROR_CODES.TOOL_CONTINUATION_ALREADY_APPLIED]: 'La réponse au widget a déjà été enregistrée.',
  [CHAT_ERROR_CODES.TOOL_CONTINUATION_INVALID_TARGET]: 'Le widget ne peut plus recevoir de réponse dans cet état.',
  [CHAT_ERROR_CODES.TOOL_CONTINUATION_UNKNOWN_TOOL_CALL]: 'Le widget demandé est introuvable.',
};

export function extractChatErrorCode(error: unknown): string | null {
  if (!error || typeof error !== 'object') {
    return null;
  }

  if ('code' in error && typeof error.code === 'string') {
    return error.code;
  }

  if (
    'error' in error
    && error.error
    && typeof error.error === 'object'
    && 'code' in error.error
    && typeof error.error.code === 'string'
  ) {
    return error.error.code;
  }

  if (error instanceof Error) {
    const parsed = parseErrorMessagePayload(error.message);
    if (parsed?.error?.code) {
      return parsed.error.code;
    }
  }

  return null;
}

function parseErrorMessagePayload(message: string): { error?: { code?: string } } | null {
  try {
    const parsed = JSON.parse(message) as unknown;
    if (
      parsed
      && typeof parsed === 'object'
      && 'error' in parsed
      && parsed.error
      && typeof parsed.error === 'object'
      && 'code' in parsed.error
      && typeof parsed.error.code === 'string'
    ) {
      return { error: { code: parsed.error.code } };
    }
  } catch {
    return null;
  }

  return null;
}

export function getChatErrorMessage(error: unknown): string {
  const code = extractChatErrorCode(error);

  if (code && code in CHAT_ERROR_MESSAGES) {
    return CHAT_ERROR_MESSAGES[code];
  }

  if (error instanceof ApiError) {
    return 'Une erreur est survenue pendant le traitement du chat.';
  }

  return 'Une erreur est survenue pendant le traitement du chat.';
}
